import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import {
  ArrowDownRightIcon,
  ArrowsLeftRightIcon,
  CheckCircleIcon,
  DeviceMobileIcon,
  PlayIcon,
} from '@phosphor-icons/react';
import {
  computeCustomerPublicId,
  type CardDetail as CardDetailEntity,
  type CardId,
} from '@voll-intelligence/domain';

import { queryKeys } from '@web/core/query';
import { boardRepository } from '@web/data/repositories';
import { t } from '@web/i18n';
import { useAuthz, useClaimCard, useOrgContext } from '@web/ui/core';
import { Button } from '@web/ui/core/atoms';
import { ClaimConfirmDialog, CloseConfirmDialog } from '@web/ui/core/molecules';

import { useCardTasks } from '../hooks/use_card_tasks';
import { useCloseCard } from '../hooks/use_close_card';
import { TransferMenu } from './transfer_menu';
import { fireAndForget } from '@web/ui/core/utils';

/**
 * A faixa de ações do card (assumir/retomar, concluir, transferir) com os
 * diálogos de confirmação. Mora fora do `CardDetail` porque é ela que detém as
 * mutações e o estado dos diálogos; enquanto o detalhe não chegou ela não
 * desenha nada, mas os hooks já rodam (as leituras começam junto com a tela).
 */
export function CardActionsRow({
  cardId,
  detail,
  onTransferred,
}: Readonly<{
  cardId: CardId;
  detail?: CardDetailEntity;
  onTransferred: () => void;
}>): React.JSX.Element | null {
  const { orgId } = useOrgContext();
  const queryClient = useQueryClient();
  // Header primary action. Backed by a CardEvent: `card.reopen` on a
  // closed card, `card.claim` (assume) on an open one the agent doesn't
  // already own. Cards already owned by a human have no primary action.
  // Invalidate the card detail + the boards so the card moves columns, and
  // the overview so "Você está atendendo" reflects it without a reload.
  const primary = useMutation({
    mutationFn: (event: PrimaryEvent) =>
      boardRepository.dispatchCardEvent(orgId, cardId, { event }),
    onSuccess: () => {
      fireAndForget(
        queryClient.invalidateQueries({ queryKey: queryKeys.cards.detail(orgId, cardId) }),
      );
      fireAndForget(queryClient.invalidateQueries({ queryKey: queryKeys.boards.all }));
      fireAndForget(queryClient.invalidateQueries({ queryKey: queryKeys.overview.all }));
    },
  });
  const claim = useClaimCard(cardId);
  const closeCard = useCloseCard(cardId);
  const { tasks, query: cardTasksQuery } = useCardTasks(cardId);

  if (!detail) return null;

  const allTasksTerminal = tasks.every((tk) => tk.status === 'done' || tk.status === 'failed');
  const concludeDisabled =
    cardTasksQuery.isPending || cardTasksQuery.isError || !allTasksTerminal || closeCard.pending;

  return (
    <div className="flex items-center justify-end gap-2 border-b border-border-soft px-5 py-2">
      <ViewAsAction detail={detail} />
      <CardHeaderActions
        detail={detail}
        onPrimary={(event) => {
          if (event.type === 'card.claim') claim.requestClaim();
          else primary.mutate(event);
        }}
        pending={primary.isPending || claim.pending}
        onTransferred={onTransferred}
        conclude={{
          visible: detail.closure === null,
          disabled: concludeDisabled,
          onClick: closeCard.requestClose,
        }}
      />
      <ClaimConfirmDialog
        open={claim.isOpen}
        pending={claim.pending}
        onConfirm={claim.confirmClaim}
        onCancel={claim.cancel}
      />
      <CloseConfirmDialog
        open={closeCard.isOpen}
        pending={closeCard.pending}
        reason={closeCard.reason}
        onReasonChange={closeCard.setReason}
        onConfirm={closeCard.confirmClose}
        onCancel={closeCard.cancel}
      />
    </div>
  );
}

type PrimaryEvent = { type: 'card.reopen' } | { type: 'card.claim' };

/**
 * "Ver como viajante" straight from the card: the URL carries only opaque ids
 * (the traveler's publicId, the card id and seq); the name is personal data and
 * rides in the history state, which the view-as page reads to prefill its dialog.
 */
function ViewAsAction({
  detail,
}: Readonly<{ detail: CardDetailEntity }>): React.JSX.Element | null {
  const { orgSlug } = useOrgContext();
  const authz = useAuthz();
  const navigate = useNavigate();
  if (!authz.has('kanban:traveler:view_as') || !detail.customer.id) return null;
  return (
    <Button
      variant="outline"
      size="sm"
      onClick={() => {
        fireAndForget(
          navigate({
            to: '/orgs/$orgSlug/view-as',
            params: { orgSlug },
            search: {
              customerId: computeCustomerPublicId(detail.customer.id),
              cardId: detail.id,
              cardSeq: String(detail.seq),
            },
            state: { travelerName: detail.customer.name },
          }),
        );
      }}
    >
      <DeviceMobileIcon size={14} weight="bold" aria-hidden />
      {t('card.actions.viewAs')}
    </Button>
  );
}

/**
 * The header primary button: its icon, label and backing CardEvent, or `null`
 * when the current state has no primary action (a card already owned by a
 * human — only transfer applies).
 */
function primaryAction(
  detail: CardDetailEntity,
): { Icon: typeof PlayIcon; label: string; event: PrimaryEvent } | null {
  if (detail.closure !== null)
    return {
      Icon: PlayIcon,
      label: t('board.drawer.action.resume'),
      event: { type: 'card.reopen' },
    };
  if (detail.assigneeAgent?.kind === 'operator') return null;
  return {
    Icon: ArrowDownRightIcon,
    label: t('board.drawer.action.assume'),
    event: { type: 'card.claim' },
  };
}

function CardHeaderActions({
  detail,
  onPrimary,
  pending,
  onTransferred,
  conclude,
}: Readonly<{
  detail: CardDetailEntity;
  onPrimary: (event: PrimaryEvent) => void;
  pending: boolean;
  onTransferred: () => void;
  conclude: { visible: boolean; disabled: boolean; onClick: () => void };
}>): React.JSX.Element {
  const primary = primaryAction(detail);
  // Closed cards can't be transferred — mirrors the backend `assertCardOpen`.
  const transferDisabled = detail.closure !== null;
  return (
    <div className="flex shrink-0 items-center gap-2">
      {primary ? (
        <Button
          variant="primary"
          size="sm"
          disabled={pending}
          aria-disabled={pending}
          onClick={() => {
            onPrimary(primary.event);
          }}
        >
          <primary.Icon size={14} weight="bold" aria-hidden />
          {primary.label}
        </Button>
      ) : null}
      {conclude.visible ? (
        <Button
          variant="primary"
          size="sm"
          disabled={conclude.disabled}
          aria-disabled={conclude.disabled}
          onClick={conclude.onClick}
          title={conclude.disabled ? t('board.drawer.conclude.disabledHint') : undefined}
        >
          <CheckCircleIcon size={14} weight="bold" aria-hidden />
          {t('board.drawer.action.conclude')}
        </Button>
      ) : null}
      {transferDisabled ? (
        <Button variant="outline" size="sm" disabled aria-disabled>
          <ArrowsLeftRightIcon size={14} weight="bold" aria-hidden />
          {t('board.drawer.action.transfer')}
        </Button>
      ) : (
        <TransferMenu detail={detail} onTransferred={onTransferred} />
      )}
    </div>
  );
}
